import type { ReactNode } from "react";

import { cn } from "../lib/utils";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "./ui/empty";

/**
 * The honest zero: what this view would show, why it's empty, and (optionally)
 * the action that fills it. Never render a bare blank table body.
 */
export function EmptyState({
  icon,
  title,
  hint,
  action,
  compact = false,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
  className?: string;
}) {
  if (compact) {
    // A secondary region's zero or failure is one line, so three of them stacked in a
    // column read as notices beside the work rather than as three pages.
    return (
      <div
        data-slot="empty"
        data-compact=""
        className={cn(
          "flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2 text-supporting",
          className,
        )}
      >
        {icon ? (
          <span
            aria-hidden="true"
            className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border bg-gradient-to-b from-card to-secondary text-foreground shadow-control [--icon-accent:hsl(var(--brand))] [&>svg]:size-4"
          >
            {icon}
          </span>
        ) : null}
        <p className="min-w-0 font-medium text-foreground">{title}</p>
        {hint ? (
          // Never narrower than a short sentence: when the row has no room, the hint
          // wraps onto its own line instead of breaking a letter per line.
          <p className="min-w-[12rem] flex-1 text-muted-foreground">{hint}</p>
        ) : null}
        {action ? <div className="ml-auto shrink-0">{action}</div> : null}
      </div>
    );
  }
  return (
    <Empty className={className}>
      {icon ? <EmptyMedia variant="icon">{icon}</EmptyMedia> : null}
      <EmptyHeader>
        <EmptyTitle>{title}</EmptyTitle>
        {hint ? <EmptyDescription>{hint}</EmptyDescription> : null}
      </EmptyHeader>
      {action ? <EmptyContent className="mt-2">{action}</EmptyContent> : null}
    </Empty>
  );
}
